import { Link } from 'react-router-dom'
import Seo, { SITE_URL } from '@/components/Seo'
import PostCard from '@/components/PostCard'
import AdSlot from '@/components/AdSlot'
import { CATEGORIES, POSTS, getCategory, getPostsByCategory } from '@/data/blog'
import NotFound from './NotFound'

function Blog({ category }: { category?: string }) {
  const active = category ? getCategory(category) : undefined

  if (category && !active) return <NotFound />

  const posts = active ? getPostsByCategory(active.slug) : POSTS
  const path = active ? `/blog/category/${active.slug}` : '/blog'
  const heading = active ? active.name : 'Dormer Guides and Cost Advice'
  const intro = active
    ? active.description
    : 'Practical, plain-language articles on dormer costs, planning rules, roof geometry and design, each linked to the free dormer calculator.'

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: heading,
    url: `${SITE_URL}${path}`,
    description: intro,
  }

  return (
    <>
      <Seo
        title={active ? `${active.name} Articles | Dormer Calculator Blog` : 'Dormer Blog: Cost, Planning and Design Guides | Dormer Calculator'}
        description={active ? active.description : 'Expert dormer guides: cost of a dormer window, planning permission, shed vs gable dormers, roof area maths and building regulations.'}
        path={path}
        schema={schema}
      />

      <div className="border-b border-steel-200 bg-white">
        <div className="container-page py-10">
          <p className="eyebrow">{active ? 'Blog category' : 'The blog'}</p>
          <h1 className="mt-2 text-3xl sm:text-4xl lg:text-5xl">{heading}</h1>
          <p className="mt-3 max-w-2xl text-lg">{intro}</p>

          <nav aria-label="Blog categories" className="mt-6 flex flex-wrap gap-2">
            <Link
              to="/blog"
              aria-current={!active ? 'page' : undefined}
              className={`rounded-full border-2 px-4 py-1.5 text-sm font-bold no-underline ${!active ? 'border-ink bg-ink text-white' : 'border-steel-300 text-steel-700 hover:border-ink'}`}
            >
              All
            </Link>
            {CATEGORIES.map(c => (
              <Link
                key={c.slug}
                to={`/blog/category/${c.slug}`}
                aria-current={active?.slug === c.slug ? 'page' : undefined}
                className={`rounded-full border-2 px-4 py-1.5 text-sm font-bold no-underline ${active?.slug === c.slug ? 'border-ink bg-ink text-white' : 'border-steel-300 text-steel-700 hover:border-ink'}`}
              >
                {c.name}
              </Link>
            ))}
          </nav>
        </div>
      </div>

      <div className="container-page py-10">
        <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_300px]">
          <div>
            {posts.length === 0 ? (
              <p className="card p-8 text-center text-steel-600">No articles in this category yet. Check back soon.</p>
            ) : (
              <div className="grid gap-6 sm:grid-cols-2">
                {posts.map(p => (
                  <PostCard key={p.slug} post={p} />
                ))}
              </div>
            )}
            <AdSlot variant="banner" />
          </div>
          <aside aria-label="Sidebar" className="space-y-6">
            <div className="card bg-ink p-5 text-white">
              <h2 className="text-xl !text-white">Price your dormer</h2>
              <p className="mt-2 text-sm text-blueprint-100">Free materials list and cost estimate in seconds.</p>
              <Link to="/calculator" className="btn btn-primary mt-4 w-full no-underline">Open calculator</Link>
            </div>
            <div className="hidden lg:block">
              <AdSlot variant="sidebar" />
            </div>
          </aside>
        </div>
      </div>
    </>
  )
}

export default Blog
